import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { displayMonth, displayShortMonthYear } from '@/lib/dates'

export interface RecognitionEntry {
  invoice_id: number
  invoice_number: number
  contact_name: string
  description: string
  amount: string
  amount_cents: number
}

interface WaterfallCell {
  month: string
  amount: string | null
  amount_cents: number | null
  entries: RecognitionEntry[]
}

export interface WaterfallAmounts {
  total: string
  total_cents: number
  recognized: string
  recognized_cents: number
  remaining: string
  remaining_cents: number
  cells: WaterfallCell[]
  entries: RecognitionEntry[]
  recognized_entries: RecognitionEntry[]
  remaining_entries: RecognitionEntry[]
}

interface WaterfallRow extends WaterfallAmounts {
  booked_month: string
}

export interface RecognitionWaterfall {
  as_of: string
  recognition_months: string[]
  invoice_count: number
  rows: WaterfallRow[]
  total: WaterfallAmounts
}

export interface RecognitionDrillDown {
  label: string
  entries: RecognitionEntry[]
}

export function RecognitionScheduleWaterfall({
  waterfall,
  onDrillDown,
}: {
  waterfall: RecognitionWaterfall
  onDrillDown: (drillDown: RecognitionDrillDown) => void
}) {
  return (
    <div
      data-testid="recognition-waterfall-scroll"
      className="mt-4 overflow-x-auto rounded-md border border-border bg-card"
    >
      <Table className="min-w-max">
        <TableHeader>
          <TableRow>
            <TableHead colSpan={2} className="sticky left-0 z-20 border-b bg-card text-center">
              Booked
            </TableHead>
            <TableHead
              colSpan={waterfall.recognition_months.length}
              className="border-b text-center"
            >
              Recognized
            </TableHead>
            <TableHead colSpan={2} className="border-b text-center">
              As of {formatMonth(waterfall.as_of)}
            </TableHead>
          </TableRow>
          <TableRow>
            <TableHead className="sticky left-0 z-20 w-32 bg-card">Month</TableHead>
            <TableHead className="sticky left-32 z-20 w-32 bg-card text-right">Total</TableHead>
            {waterfall.recognition_months.map((month) => (
              <TableHead
                key={month}
                className={cn(
                  'min-w-28 text-right',
                  month === waterfall.as_of && 'bg-primary/10 font-medium text-foreground',
                )}
              >
                {shortMonth(month)}
              </TableHead>
            ))}
            <TableHead className="min-w-32 text-right">Recognized</TableHead>
            <TableHead className="min-w-32 text-right">Remaining</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {waterfall.rows.map((row) => (
            <WaterfallRowView key={row.booked_month} row={row} onDrillDown={onDrillDown} />
          ))}
          <WaterfallTotalRow total={waterfall.total} onDrillDown={onDrillDown} />
        </TableBody>
      </Table>
    </div>
  )
}

function WaterfallRowView({
  row,
  onDrillDown,
}: {
  row: WaterfallRow
  onDrillDown: (drillDown: RecognitionDrillDown) => void
}) {
  const label = formatMonth(row.booked_month)
  return (
    <TableRow>
      <TableCell className="sticky left-0 z-10 w-32 bg-card font-medium">{label}</TableCell>
      <AmountCell
        amount={row.total}
        entries={row.entries}
        label={`${label} Total`}
        sticky
        onDrillDown={onDrillDown}
      />
      {row.cells.map((cell) => (
        <AmountCell
          key={cell.month}
          amount={cell.amount}
          entries={cell.entries}
          label={`${label} ${formatMonth(cell.month)}`}
          onDrillDown={onDrillDown}
        />
      ))}
      <AmountCell
        amount={row.recognized}
        entries={row.recognized_entries}
        label={`${label} Recognized`}
        onDrillDown={onDrillDown}
      />
      <AmountCell
        amount={row.remaining}
        entries={row.remaining_entries}
        label={`${label} Remaining`}
        onDrillDown={onDrillDown}
      />
    </TableRow>
  )
}

function WaterfallTotalRow({
  total,
  onDrillDown,
}: {
  total: WaterfallAmounts
  onDrillDown: (drillDown: RecognitionDrillDown) => void
}) {
  return (
    <TableRow className="border-t-2 font-semibold hover:bg-transparent">
      <TableCell className="sticky left-0 z-10 bg-card">Total</TableCell>
      <AmountCell
        amount={total.total}
        entries={total.entries}
        label="Total Booked"
        sticky
        onDrillDown={onDrillDown}
      />
      {total.cells.map((cell) => (
        <AmountCell
          key={cell.month}
          amount={cell.amount}
          entries={cell.entries}
          label={`Total ${formatMonth(cell.month)}`}
          onDrillDown={onDrillDown}
        />
      ))}
      <AmountCell
        amount={total.recognized}
        entries={total.recognized_entries}
        label="Total Recognized"
        onDrillDown={onDrillDown}
      />
      <AmountCell
        amount={total.remaining}
        entries={total.remaining_entries}
        label="Total Remaining"
        onDrillDown={onDrillDown}
      />
    </TableRow>
  )
}

function AmountCell({
  amount,
  entries,
  label,
  sticky = false,
  onDrillDown,
}: {
  amount: string | null
  entries: RecognitionEntry[]
  label: string
  sticky?: boolean
  onDrillDown: (drillDown: RecognitionDrillDown) => void
}) {
  return (
    <TableCell
      className={cn(
        'text-right tabular-nums',
        sticky && 'sticky left-32 z-10 w-32 bg-card',
        amount?.startsWith('−') && 'text-destructive',
      )}
    >
      {amount !== null && entries.length > 0 ? (
        <Button
          type="button"
          variant="link"
          size="inline"
          className="font-inherit text-inherit underline underline-offset-2"
          aria-label={`Drill into ${label} ${amount}`}
          onClick={() => onDrillDown({ label, entries })}
        >
          {amount}
        </Button>
      ) : (
        amount
      )}
    </TableCell>
  )
}

export function RecognitionInvoiceDrillDown({
  drillDown,
  onOpenChange,
  onOpenInvoice,
}: {
  drillDown: RecognitionDrillDown | null
  onOpenChange: (open: boolean) => void
  onOpenInvoice: (invoiceId: number) => void
}) {
  return (
    <Dialog open={drillDown !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contributing invoices</DialogTitle>
          <DialogDescription>{drillDown?.label}</DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {drillDown?.entries.map((entry) => (
              <TableRow
                key={entry.invoice_id}
                className="cursor-pointer"
                onClick={() => onOpenInvoice(entry.invoice_id)}
              >
                <TableCell>
                  <Button
                    type="button"
                    variant="link"
                    size="inline"
                    onClick={(event) => {
                      event.stopPropagation()
                      onOpenInvoice(entry.invoice_id)
                    }}
                  >
                    INV-{entry.invoice_number}
                  </Button>
                </TableCell>
                <TableCell>{entry.contact_name}</TableCell>
                <TableCell className="text-right tabular-nums">{entry.amount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  )
}

export function formatRecognitionMonth(month: string) {
  return displayShortMonthYear(`${month}-01`)
}

function formatMonth(month: string) {
  return formatRecognitionMonth(month)
}

function shortMonth(month: string) {
  return displayMonth(month)
}
